/** Zero-based cell address. */
export interface CellAddress {
  row: number;
  col: number;
}

/** Selection between an anchor and a focus cell (either corner, as the user dragged it). */
export interface CellRange {
  anchor: CellAddress;
  focus: CellAddress;
}

/** Normalised rectangle, inclusive bounds. */
export interface Rect {
  top: number;
  left: number;
  bottom: number;
  right: number;
}

/** Normalises a range to a rectangle with top ≤ bottom and left ≤ right. */
export function normalizeRange(range: CellRange): Rect {
  return {
    top: Math.min(range.anchor.row, range.focus.row),
    left: Math.min(range.anchor.col, range.focus.col),
    bottom: Math.max(range.anchor.row, range.focus.row),
    right: Math.max(range.anchor.col, range.focus.col),
  };
}

/** Clamps a rectangle to a grid of `rows` × `cols`; undefined if nothing remains. */
export function clampRect(rect: Rect, rows: number, cols: number): Rect | undefined {
  const r: Rect = {
    top: Math.max(0, rect.top),
    left: Math.max(0, rect.left),
    bottom: Math.min(rows - 1, rect.bottom),
    right: Math.min(cols - 1, rect.right),
  };
  return r.top > r.bottom || r.left > r.right ? undefined : r;
}

/** True if the cell lies inside the rectangle. */
export function rectContains(rect: Rect, cell: CellAddress): boolean {
  return (
    cell.row >= rect.top &&
    cell.row <= rect.bottom &&
    cell.col >= rect.left &&
    cell.col <= rect.right
  );
}

/** All cells of a rectangle, row by row. */
export function rectCells(rect: Rect): CellAddress[] {
  const cells: CellAddress[] = [];
  for (let row = rect.top; row <= rect.bottom; row++) {
    for (let col = rect.left; col <= rect.right; col++) cells.push({ row, col });
  }
  return cells;
}

/** Single-cell range. */
export function cellRange(cell: CellAddress): CellRange {
  return { anchor: cell, focus: cell };
}

/** Shift+click / Shift+arrow: keeps the anchor and moves the focus. */
export function extendRange(range: CellRange, focus: CellAddress): CellRange {
  return { anchor: range.anchor, focus };
}

/** Moves a cell by (dRow, dCol), clamped to the grid. */
export function moveCell(
  cell: CellAddress,
  dRow: number,
  dCol: number,
  rows: number,
  cols: number,
): CellAddress {
  return {
    row: Math.min(Math.max(cell.row + dRow, 0), Math.max(rows - 1, 0)),
    col: Math.min(Math.max(cell.col + dCol, 0), Math.max(cols - 1, 0)),
  };
}
